import api from './api';

/**
 * Test della connessione browser → server dell'agenzia (Impostazioni, solo titolare).
 * Le funzioni pure (calcolo e formato) sono separate dal runner per poterle testare.
 */

export const MB = 1024 * 1024;

export interface RisultatoConnessione {
    latenzaMs: number;
    downloadMbps: number;
    uploadMbps: number;
}

export type FaseTest = 'latenza' | 'download' | 'upload';

/** Mbit/s da byte trasferiti e millisecondi impiegati; 0 se il tempo non è valido. */
export const calcolaMbps = (bytes: number, ms: number): number => {
    if (!(ms > 0) || !(bytes > 0)) return 0;
    return (bytes * 8) / 1_000_000 / (ms / 1000);
};

/** "6,2" sotto 100 Mbit/s, intero sopra; mai "0,0" per connessioni lentissime. */
export const formatMbps = (mbps: number): string => {
    if (!(mbps > 0)) return '0';
    if (mbps >= 100) return Math.round(mbps).toLocaleString('it-IT');
    if (mbps < 0.1) return '< 0,1';
    return mbps.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

/** Durata leggibile: "meno di 1 s", "45 s", "1 min 40 s", "2 min", "1 h 5 min". */
export const formatDurata = (secondi: number): string => {
    if (!isFinite(secondi) || secondi < 0) return '—';
    const s = Math.round(secondi);
    if (s < 1) return 'meno di 1 s';
    if (s < 60) return `${s} s`;
    if (s < 3600) {
        const min = Math.floor(s / 60);
        const resto = s % 60;
        return resto ? `${min} min ${resto} s` : `${min} min`;
    }
    const h = Math.floor(s / 3600);
    const min = Math.round((s % 3600) / 60);
    return min ? `${h} h ${min} min` : `${h} h`;
};

/** Secondi per scaricare un file da {@code mb} MB alla velocità indicata (Infinity se 0). */
export const secondiPerScaricare = (mb: number, mbps: number): number => {
    if (!(mbps > 0)) return Infinity;
    return (mb * MB * 8) / 1_000_000 / mbps;
};

export const testoRisultato = (r: RisultatoConnessione): string =>
    `Download ${formatMbps(r.downloadMbps)} Mbit/s · Upload ${formatMbps(r.uploadMbps)} Mbit/s · Latenza ${Math.round(r.latenzaMs)} ms`;

export const testoStime = (downloadMbps: number): string => {
    if (!(downloadMbps > 0)) return 'Non è stato possibile stimare i tempi di apertura.';
    return `Una relazione da 50 MB si apre in circa ${formatDurata(secondiPerScaricare(50, downloadMbps))}; `
        + `da 100 MB in circa ${formatDurata(secondiPerScaricare(100, downloadMbps))}.`;
};

/**
 * Dimensione del blocco successivo perché duri circa {@code msObiettivo}, dato l'ultimo
 * throughput misurato; limitata fra {@code minBytes} e {@code maxBytes}.
 */
export const prossimoBlocco = (
    mbpsMisurati: number, msObiettivo: number, minBytes: number, maxBytes: number,
): number => {
    if (!(mbpsMisurati > 0)) return minBytes;
    const bytes = (mbpsMisurati * 1_000_000 / 8) * (msObiettivo / 1000);
    return Math.max(minBytes, Math.min(maxBytes, Math.round(bytes)));
};

// ─── Runner ───────────────────────────────────────────────────────────────

const PING_N = 5;
const DOWNLOAD_MS = 9000;
const UPLOAD_MS = 5000;
const BLOCCO_MS = 1500;
const BLOCCO_MIN = MB / 4;
const BLOCCO_MAX = 20 * MB;
// Tetti di sicurezza sul volume totale per test (connessioni molto veloci).
const DOWNLOAD_CAP = 250 * MB;
const UPLOAD_CAP = 120 * MB;
/** Durata nominale complessiva, usata per la barra di avanzamento. */
export const DURATA_STIMATA_MS = 1000 + DOWNLOAD_MS + UPLOAD_MS;

export class TestConnessioneError extends Error {}

const stato = (e: any): number | undefined => e?.response?.status;

const messaggioErrore = (e: any): string =>
    e?.response?.data?.message
    || (e?.response ? 'Il server ha interrotto il test.' : 'Connessione al server non riuscita.');

const casuali = (n: number): Uint8Array => {
    const out = new Uint8Array(n);
    // getRandomValues accetta al massimo 64 KB per chiamata.
    for (let i = 0; i < n; i += 65536) {
        crypto.getRandomValues(out.subarray(i, Math.min(n, i + 65536)));
    }
    return out;
};

/**
 * Esegue latenza, download e upload in sequenza. {@code onAvanzamento} riceve la fase e
 * una frazione 0..1 dell'intero test. Un 429 a metà misura chiude la fase con i dati raccolti.
 */
export const eseguiTestConnessione = async (
    onAvanzamento: (fase: FaseTest, frazione: number) => void,
): Promise<RisultatoConnessione> => {
    const inizio = performance.now();
    const avanza = (fase: FaseTest) =>
        onAvanzamento(fase, Math.min(0.99, (performance.now() - inizio) / DURATA_STIMATA_MS));
    const opz = { skipGlobalErrorToast: true } as const;

    // Latenza: il primo ping registra l'avvio (ed è escluso dalla media).
    onAvanzamento('latenza', 0);
    try {
        await api.get('/connessione-test/ping', { ...opz, params: { avvio: true } });
    } catch (e) {
        throw new TestConnessioneError(messaggioErrore(e));
    }
    const tempi: number[] = [];
    for (let i = 0; i < PING_N; i++) {
        const t0 = performance.now();
        try {
            await api.get('/connessione-test/ping', { ...opz, params: { _: Date.now() } });
        } catch (e) {
            if (stato(e) === 429) break;
            throw new TestConnessioneError(messaggioErrore(e));
        }
        tempi.push(performance.now() - t0);
        avanza('latenza');
    }
    const latenzaMs = tempi.length ? tempi.reduce((a, b) => a + b, 0) / tempi.length : 0;

    // Download: il primo blocco fa da riscaldamento e non entra nel calcolo.
    let dBytes = 0, dMs = 0, scaricati = 0, primo = true;
    let blocco = BLOCCO_MIN, riscaldamento = { bytes: 0, ms: 0 };
    const fineDownload = performance.now() + DOWNLOAD_MS;
    while (performance.now() < fineDownload && scaricati < DOWNLOAD_CAP) {
        const mb = Math.round((blocco / MB) * 100) / 100;
        const t0 = performance.now();
        let bytes: number;
        try {
            const r = await api.get<ArrayBuffer>('/connessione-test/download', {
                ...opz, params: { mb, _: Date.now() }, responseType: 'arraybuffer',
            });
            bytes = r.data.byteLength;
        } catch (e) {
            if (stato(e) === 429) break;
            throw new TestConnessioneError(messaggioErrore(e));
        }
        const ms = performance.now() - t0;
        scaricati += bytes;
        if (primo) {
            primo = false;
            riscaldamento = { bytes, ms };
        } else {
            dBytes += bytes;
            dMs += ms;
        }
        blocco = prossimoBlocco(calcolaMbps(bytes, ms), BLOCCO_MS, BLOCCO_MIN, BLOCCO_MAX);
        avanza('download');
    }
    // Connessione così lenta da non andare oltre il riscaldamento: si usa quello.
    const downloadMbps = dMs > 0 ? calcolaMbps(dBytes, dMs) : calcolaMbps(riscaldamento.bytes, riscaldamento.ms);

    // Upload: stesso schema, con un buffer casuale generato una volta sola.
    const sorgente = casuali(BLOCCO_MAX);
    let uBytes = 0, uMs = 0, caricati = 0;
    primo = true;
    blocco = BLOCCO_MIN;
    riscaldamento = { bytes: 0, ms: 0 };
    const fineUpload = performance.now() + UPLOAD_MS;
    while (performance.now() < fineUpload && caricati < UPLOAD_CAP) {
        const corpo = new Blob([sorgente.slice(0, blocco)], { type: 'application/octet-stream' });
        const t0 = performance.now();
        try {
            await api.post('/connessione-test/upload', corpo, {
                ...opz, headers: { 'Content-Type': 'application/octet-stream' },
            });
        } catch (e) {
            if (stato(e) === 429) break;
            throw new TestConnessioneError(messaggioErrore(e));
        }
        const ms = performance.now() - t0;
        caricati += blocco;
        if (primo) {
            primo = false;
            riscaldamento = { bytes: blocco, ms };
        } else {
            uBytes += blocco;
            uMs += ms;
        }
        blocco = prossimoBlocco(calcolaMbps(blocco, ms), BLOCCO_MS, BLOCCO_MIN, BLOCCO_MAX);
        avanza('upload');
    }
    const uploadMbps = uMs > 0 ? calcolaMbps(uBytes, uMs) : calcolaMbps(riscaldamento.bytes, riscaldamento.ms);

    return { latenzaMs, downloadMbps, uploadMbps };
};
